// src/pages/agent-booking-system-sabre-integration/components/AgentDashboard.jsx
import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const AgentDashboard = ({ user, tenant, metrics, corporateAccounts, onRefresh }) => {
  const [recentActivity, setRecentActivity] = useState([]);
  const [priorityBookings, setPriorityBookings] = useState([]);

  useEffect(() => {
    // Mock recent activity - in real app, this would come from API
    setRecentActivity([
      {
        id: 1,
        type: 'booking_confirmed',
        description: 'Flight booking confirmed for John Smith',
        timestamp: new Date(Date.now() - 15 * 60 * 1000),
        priority: 'normal'
      },
      {
        id: 2,
        type: 'approval_required',
        description: 'Travel request requires manager approval',
        timestamp: new Date(Date.now() - 45 * 60 * 1000),
        priority: 'high'
      },
      {
        id: 3,
        type: 'booking_cancelled',
        description: 'Hotel booking cancelled due to policy violation',
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
        priority: 'medium'
      }
    ]);

    // Mock priority bookings
    setPriorityBookings([
      {
        id: 1,
        traveler: 'Sarah Johnson',
        destination: 'London, UK',
        departure: '2025-01-15',
        priority: 'urgent',
        slaRemaining: '4h 23m'
      },
      {
        id: 2,
        traveler: 'Mike Chen',
        destination: 'Tokyo, Japan',
        departure: '2025-01-20',
        priority: 'high',
        slaRemaining: '12h 45m'
      }
    ]);
  }, []);

  const getActivityIcon = (type) => {
    switch (type) {
      case 'booking_confirmed': return 'CheckCircle';
      case 'approval_required': return 'Clock';
      case 'booking_cancelled': return 'XCircle';
      default: return 'Bell';
    }
  };

  const getActivityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-600';
      case 'medium': return 'text-yellow-600';
      case 'urgent': return 'text-red-700';
      default: return 'text-text-secondary';
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const diffInMinutes = Math.floor((now - timestamp) / (1000 * 60));
    
    if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    } else if (diffInMinutes < 1440) {
      return `${Math.floor(diffInMinutes / 60)}h ago`;
    } else {
      return `${Math.floor(diffInMinutes / 1440)}d ago`;
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Welcome Section */}
      <div className="mb-8">
        <h1 className="text-2xl font-heading-bold text-text-primary mb-2">
          Welcome back, {user?.full_name?.split(' ')?.[0] || 'Agent'}
        </h1>
        <p className="text-text-secondary">
          Here's what's happening with your corporate travel bookings today.
        </p>
      </div>
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-surface rounded-lg border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Icon name="Calendar" size={24} className="text-blue-600" />
            </div>
            <div className="text-right">
              <div className="text-2xl font-heading-bold text-text-primary">
                {metrics?.bookingsToday || 12}
              </div>
              <div className="text-sm text-text-secondary">Today's Bookings</div>
            </div>
          </div>
          <div className="text-sm text-green-600">
            <Icon name="TrendingUp" size={14} className="inline mr-1" />
            +8% from yesterday
          </div>
        </div>

        <div className="bg-surface rounded-lg border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Icon name="DollarSign" size={24} className="text-green-600" />
            </div>
            <div className="text-right">
              <div className="text-2xl font-heading-bold text-text-primary">
                ${(metrics?.totalSavings || 15400)?.toLocaleString()}
              </div>
              <div className="text-sm text-text-secondary">Cost Savings</div>
            </div>
          </div>
          <div className="text-sm text-green-600">
            <Icon name="TrendingUp" size={14} className="inline mr-1" />
            This month
          </div>
        </div>

        <div className="bg-surface rounded-lg border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Icon name="Clock" size={24} className="text-purple-600" />
            </div>
            <div className="text-right">
              <div className="text-2xl font-heading-bold text-text-primary">
                {metrics?.avgResponseTime || '2.5'}m
              </div>
              <div className="text-sm text-text-secondary">Avg Response</div>
            </div>
          </div>
          <div className="text-sm text-green-600">
            <Icon name="TrendingDown" size={14} className="inline mr-1" />
            -12% improvement
          </div>
        </div>

        <div className="bg-surface rounded-lg border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <Icon name="Star" size={24} className="text-orange-600" />
            </div>
            <div className="text-right">
              <div className="text-2xl font-heading-bold text-text-primary">
                {metrics?.satisfactionScore || '4.8'}
              </div>
              <div className="text-sm text-text-secondary">Satisfaction</div>
            </div>
          </div>
          <div className="text-sm text-green-600">
            <Icon name="TrendingUp" size={14} className="inline mr-1" />
            +0.3 this week
          </div>
        </div>
      </div>
      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Priority Bookings */}
        <div className="lg:col-span-2">
          <div className="bg-surface rounded-lg border border-border">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-heading-semibold text-text-primary">
                  Priority Booking Queue
                </h2>
                <button 
                  onClick={onRefresh}
                  className="text-sm text-primary-600 hover:text-primary-700 font-body-medium"
                >
                  Refresh
                </button>
              </div>
            </div>
            
            <div className="divide-y divide-border">
              {priorityBookings?.length > 0 ? priorityBookings?.map((booking) => (
                <div key={booking?.id} className="p-6 hover:bg-secondary-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <div className={`w-2 h-2 rounded-full ${
                          booking?.priority === 'urgent' ? 'bg-red-500' :
                          booking?.priority === 'high'? 'bg-orange-500' : 'bg-yellow-500'
                        }`} />
                        <h3 className="font-heading-semibold text-text-primary">
                          {booking?.traveler}
                        </h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-body-medium ${
                          booking?.priority === 'urgent' ? 'bg-red-100 text-red-800' :
                          booking?.priority === 'high'? 'bg-orange-100 text-orange-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {booking?.priority?.toUpperCase()}
                        </span>
                      </div>
                      <div className="text-text-secondary text-sm space-y-1">
                        <div className="flex items-center space-x-2">
                          <Icon name="MapPin" size={14} />
                          <span>Destination: {booking?.destination}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Icon name="Calendar" size={14} />
                          <span>Departure: {new Date(booking.departure)?.toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-body-medium text-text-secondary mb-1">
                        SLA Remaining
                      </div>
                      <div className="text-lg font-heading-semibold text-primary-600">
                        {booking?.slaRemaining}
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 flex space-x-3">
                    <button className="px-4 py-2 bg-primary-600 text-white text-sm font-body-medium rounded-lg hover:bg-primary-700 transition-colors">
                      Start Booking
                    </button>
                    <button className="px-4 py-2 bg-secondary-100 text-text-secondary text-sm font-body-medium rounded-lg hover:bg-secondary-200 transition-colors">
                      View Details
                    </button>
                  </div>
                </div>
              )) : (
                <div className="p-8 text-center">
                  <Icon name="CheckCircle" size={48} className="text-green-500 mx-auto mb-4" />
                  <h3 className="text-lg font-heading-semibold text-text-primary mb-2">
                    All caught up!
                  </h3>
                  <p className="text-text-secondary">
                    No priority bookings in your queue right now.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div>
          <div className="bg-surface rounded-lg border border-border">
            <div className="p-6 border-b border-border">
              <h2 className="text-lg font-heading-semibold text-text-primary">
                Recent Activity
              </h2>
            </div>
            
            <div className="divide-y divide-border">
              {recentActivity?.length > 0 ? recentActivity?.map((activity) => (
                <div key={activity?.id} className="p-4">
                  <div className="flex items-start space-x-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      activity?.priority === 'high' ? 'bg-red-100' :
                      activity?.priority === 'medium'? 'bg-yellow-100' : 'bg-gray-100'
                    }`}>
                      <Icon 
                        name={getActivityIcon(activity?.type)} 
                        size={16} 
                        className={getActivityColor(activity?.priority)}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-text-primary font-body-medium">
                        {activity?.description}
                      </p>
                      <p className="text-xs text-text-secondary mt-1">
                        {formatTimeAgo(activity?.timestamp)}
                      </p>
                    </div>
                  </div>
                </div>
              )) : (
                <div className="p-6 text-center">
                  <Icon name="Bell" size={32} className="text-text-secondary mx-auto mb-2" />
                  <p className="text-text-secondary text-sm">
                    No recent activity
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mt-6 space-y-3">
            <button className="w-full px-4 py-3 bg-primary-600 text-white text-sm font-body-medium rounded-lg hover:bg-primary-700 transition-colors flex items-center justify-center space-x-2">
              <Icon name="Plus" size={16} />
              <span>New Booking</span>
            </button>
            <button className="w-full px-4 py-3 bg-surface border border-border text-text-primary text-sm font-body-medium rounded-lg hover:bg-secondary-50 transition-colors flex items-center justify-center space-x-2">
              <Icon name="Search" size={16} />
              <span>Search Inventory</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentDashboard;